import React, { useState, useEffect } from 'react'
import { analyticsAPI } from '../services/api'
import { ScatterChart, createResilienceScatterData } from '../components/Chart'
import { theme } from '../theme'

const NewUserPage = () => {
  const [userEntries, setUserEntries] = useState([])
  const [syntheticEntries, setSyntheticEntries] = useState([])
  const [analysis, setAnalysis] = useState(null)
  const [scatterData, setScatterData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [confidenceLevels, setConfidenceLevels] = useState(null)

  // Load confidence level information on component mount
  useEffect(() => {
    loadConfidenceLevels()
  }, [])

  // Auto-analyze when entries change
  useEffect(() => {
    if (userEntries.length > 0 || syntheticEntries.length > 0) {
      performAnalysis()
    } else {
      setAnalysis(null)
      setScatterData(null)
    }
  }, [userEntries, syntheticEntries])

  const loadConfidenceLevels = async () => {
    try {
      const response = await analyticsAPI.getConfidenceLevels()
      setConfidenceLevels(response.data.data)
    } catch (err) {
      console.error('Failed to load confidence levels:', err)
    }
  }

  const generateSyntheticEntry = async () => {
    setLoading(true)
    setError(null)
    
    try {
      const response = await analyticsAPI.generateSyntheticEntries(1)
      const newEntry = response.data.data.entries[0]
      setSyntheticEntries(prev => [...prev, newEntry])
    } catch (err) {
      setError('Failed to generate synthetic entry: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const performAnalysis = async () => {
    if (userEntries.length === 0 && syntheticEntries.length === 0) return
    
    setLoading(true)
    setError(null)
    
    try {
      const response = await analyticsAPI.analyzeNewUser(userEntries, syntheticEntries)
      setAnalysis(response.data.data.analysis)
      setScatterData(response.data.data.scatterData)
    } catch (err) {
      setError('Failed to analyze data: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const clearAllTestData = () => {
    setSyntheticEntries([])
    setUserEntries([])
  }

  const addManualEntry = () => {
    // Create a simple manual entry form
    const pressure = parseFloat(prompt('Enter pressure level (0-10):'))
    if (isNaN(pressure) || pressure < 0 || pressure > 10) {
      alert('Invalid pressure level. Must be between 0 and 10.')
      return
    }

    const entry = {
      id: `manual_${Date.now()}`,
      pressure,
      hours: parseFloat(prompt('Enter hours score (0-10):')) || 5,
      focus: parseFloat(prompt('Enter focus score (0-10):')) || 5,
      calm: parseFloat(prompt('Enter calm score (0-10):')) || 5,
      happiness: parseFloat(prompt('Enter happiness score (0-10):')) || 5,
      depression: parseFloat(prompt('Enter depression score (0-10):')) || 5,
      mania: parseFloat(prompt('Enter mania score (0-10):')) || 5,
      energy: parseFloat(prompt('Enter energy score (0-10):')) || 5,
      createdAt: new Date().toISOString(),
      isManual: true
    }

    // Validate all entries are within range
    const factors = ['hours', 'focus', 'calm', 'happiness', 'depression', 'mania', 'energy']
    const valid = factors.every(factor => 
      !isNaN(entry[factor]) && entry[factor] >= 0 && entry[factor] <= 10
    )

    if (valid) {
      setUserEntries(prev => [...prev, entry])
    } else {
      alert('Invalid entry. All scores must be between 0 and 10.')
    }
  }

  const getConfidenceColor = (level) => {
    if (!confidenceLevels) return theme.colors.primary.main
    return confidenceLevels.levels[level]?.color || theme.colors.primary.main
  }

  const getScoreInterpretation = (score) => {
    if (score >= 85) return { text: 'Superhuman', color: '#2ed573' }
    if (score >= 50) return { text: 'Moderately affected by pressure', color: '#3742fa' }
    if (score >= 25) return { text: 'Notably decreased by pressure', color: '#ffa502' }
    return { text: 'Emotions crash under pressure', color: '#ff4757' }
  }

  const createChartData = () => {
    if (!scatterData) return { datasets: [] }

    const colors = {
      real: theme.colors.primary.main,
      synthetic: theme.colors.secondary.green + '80'
    }

    const datasets = []

    if (scatterData.real.length > 0) {
      datasets.push({
        label: 'Your Entries',
        data: scatterData.real,
        backgroundColor: colors.real,
        borderColor: colors.real,
        borderWidth: 2,
        pointRadius: 8,
        pointHoverRadius: 10,
      })
    }

    if (scatterData.synthetic.length > 0) {
      datasets.push({
        label: 'Generated Examples',
        data: scatterData.synthetic,
        backgroundColor: colors.synthetic,
        borderColor: theme.colors.secondary.green,
        borderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
        pointStyle: 'triangle',
      })
    }

    return { datasets }
  }

  return (
    <div style={{ 
      padding: '24px', 
      maxWidth: '1200px', 
      margin: '0 auto',
      backgroundColor: '#f8f9fa',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ 
          color: theme.colors.primary.main,
          fontSize: '28px',
          fontWeight: '600',
          marginBottom: '8px'
        }}>
          New User Mental Resilience Analysis
        </h1>
        <p style={{ 
          color: theme.colors.secondary.black,
          fontSize: '16px',
          lineHeight: '1.5'
        }}>
          Get early insights into your mental resilience with limited data. Add your journal entries or generate synthetic examples to see how your emotions respond to pressure.
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div style={{
          backgroundColor: '#ffe6e6',
          border: '1px solid #ff4757',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px',
          color: '#d63031'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ 
        display: 'flex', 
        gap: '16px', 
        marginBottom: '32px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={addManualEntry}
          style={{
            backgroundColor: theme.colors.primary.main,
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = theme.colors.primary.dark}
          onMouseOut={(e) => e.target.style.backgroundColor = theme.colors.primary.main}
        >
          Add Manual Entry
        </button>

        <button
          onClick={generateSyntheticEntry}
          disabled={loading}
          style={{
            backgroundColor: theme.colors.secondary.green,
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '500',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1,
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => !loading && (e.target.style.backgroundColor = '#00a085')}
          onMouseOut={(e) => !loading && (e.target.style.backgroundColor = theme.colors.secondary.green)}
        >
          {loading ? 'Generating...' : 'Generate Synthetic Entry'}
        </button>

        {(userEntries.length > 0 || syntheticEntries.length > 0) && (
          <button
            onClick={clearAllTestData}
            style={{
              backgroundColor: '#ff4757',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#e84057'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#ff4757'}
          >
            Clear Test Data
          </button>
        )}
      </div>

      {/* Main Content Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        
        {/* Left Column - Analysis Results */}
        <div>
          {/* Current Entries Summary */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ 
              color: theme.colors.primary.main,
              fontSize: '20px',
              fontWeight: '600',
              marginBottom: '16px'
            }}>
              Current Data
            </h3>
            
            <div style={{ display: 'flex', gap: '24px' }}>
              <div>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: '700', 
                  color: theme.colors.primary.main 
                }}>
                  {userEntries.length}
                </div>
                <div style={{ 
                  fontSize: '14px', 
                  color: theme.colors.secondary.black 
                }}>
                  Your Entries
                </div>
              </div>
              
              <div>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: '700', 
                  color: theme.colors.secondary.green 
                }}>
                  {syntheticEntries.length}
                </div>
                <div style={{ 
                  fontSize: '14px', 
                  color: theme.colors.secondary.black 
                }}>
                  Generated Examples
                </div>
              </div>
            </div>
          </div>

          {/* Mental Resilience Analysis */}
          {analysis && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ 
                color: theme.colors.primary.main,
                fontSize: '20px',
                fontWeight: '600',
                marginBottom: '16px'
              }}>
                Mental Resilience Estimate
              </h3>
              
              {/* Score Display */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ 
                  fontSize: '48px', 
                  fontWeight: '700', 
                  color: getScoreInterpretation(analysis.score).color,
                  lineHeight: '1'
                }}>
                  {analysis.score?.toFixed(1) || 'N/A'}
                </div>
                <div style={{ 
                  fontSize: '16px', 
                  color: getScoreInterpretation(analysis.score).color,
                  fontWeight: '500',
                  marginTop: '4px'
                }}>
                  {getScoreInterpretation(analysis.score).text}
                </div>
              </div>

              {/* Confidence Level */}
              <div style={{ 
                backgroundColor: getConfidenceColor(analysis.confidence.level) + '20',
                border: `2px solid ${getConfidenceColor(analysis.confidence.level)}`,
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                                 <div style={{ 
                   fontSize: '16px', 
                   fontWeight: '600',
                   color: getConfidenceColor(analysis.confidence.level),
                   marginBottom: '8px'
                 }}>
                   Confidence: {analysis.confidence.level?.replace('-', ' ').toUpperCase() || 'UNKNOWN'}
                 </div>
                <div style={{ 
                  fontSize: '14px', 
                  color: theme.colors.secondary.black,
                  lineHeight: '1.4'
                }}>
                  {analysis.confidence.message}
                </div>
              </div>

              {/* Interpretation */}
              <div style={{ 
                fontSize: '14px', 
                color: theme.colors.secondary.black,
                fontStyle: 'italic',
                marginBottom: '16px'
              }}>
                {analysis.interpretation}
              </div>

              {/* Statistical Information */}
              {analysis.confidence.statistical && (
                <div style={{
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  padding: '12px',
                  fontSize: '12px',
                  color: '#495057',
                  marginBottom: '12px'
                }}>
                  <div style={{ fontWeight: '600', marginBottom: '8px' }}>Statistical Analysis:</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <div>
                      <strong>R-squared:</strong> {analysis.rSquared ? (analysis.rSquared * 100).toFixed(1) + '%' : 'N/A'}
                    </div>
                    <div>
                      <strong>P-value:</strong> {analysis.confidence.statistical.pValue || 'N/A'}
                    </div>
                    <div>
                      <strong>Significance:</strong> {analysis.confidence.statistical.significanceLevel || 'N/A'}
                    </div>
                    <div>
                      <strong>Sample Quality:</strong> {analysis.confidence.statistical.sampleSizeAdequacy || 'N/A'}
                    </div>
                  </div>
                  {analysis.confidence.statistical.pValue && (
                    <div style={{ marginTop: '8px', fontSize: '11px', fontStyle: 'italic' }}>
                      {analysis.confidence.statistical.significanceLevel === 'significant' 
                        ? '✓ Statistically significant pattern detected' 
                        : analysis.confidence.statistical.significanceLevel === 'marginal'
                        ? '~ Marginal statistical pattern emerging'
                        : '○ Pattern not yet statistically significant'}
                    </div>
                  )}
                </div>
              )}

              {/* Bayesian Weighting Info */}
              {analysis.bayesianAdjustment && (
                <div style={{
                  backgroundColor: analysis.bayesianAdjustment.syntheticDataUsed ? '#f0f8ff' : '#f8f9fa',
                  border: `1px solid ${analysis.bayesianAdjustment.syntheticDataUsed ? '#3742fa' : '#dee2e6'}`,
                  borderRadius: '6px',
                  padding: '12px',
                  fontSize: '12px',
                  color: analysis.bayesianAdjustment.syntheticDataUsed ? '#3742fa' : '#495057'
                }}>
                  <div style={{ fontWeight: '600', marginBottom: '8px' }}>
                    Bayesian Analysis (Statistical Weighting):
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                    <div>
                      <strong>Personal Data:</strong> {(analysis.bayesianAdjustment.dataWeight * 100).toFixed(1)}%
                    </div>
                    <div>
                      <strong>Population Priors:</strong> {(analysis.bayesianAdjustment.priorWeight * 100).toFixed(1)}%
                    </div>
                  </div>
                  {analysis.bayesianAdjustment.statisticalBasis && (
                    <div style={{ fontSize: '11px', fontStyle: 'italic' }}>
                      Weighting based on statistical significance (p={analysis.bayesianAdjustment.statisticalBasis.pValue}) 
                      and sample quality ({analysis.bayesianAdjustment.statisticalBasis.sampleAdequacy})
                    </div>
                  )}
                  {analysis.bayesianAdjustment.syntheticDataUsed && (
                    <div style={{ marginTop: '6px', fontSize: '11px', fontStyle: 'italic' }}>
                      Note: Includes synthetic data to improve pattern analysis
                    </div>
                  )}
                </div>
              )}

              {/* Enhanced Detailed Bayesian Analysis */}
              {analysis.bayesianAdjustment?.detailedAnalysis && (
                <div style={{
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  padding: '16px',
                  marginTop: '16px',
                  fontSize: '13px',
                  color: '#495057'
                }}>
                  <div style={{ fontWeight: '700', marginBottom: '12px', color: theme.colors.primary.main }}>
                    🧠 Detailed Prior Analysis
                  </div>
                  
                  {/* Pattern Matching Summary */}
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontWeight: '600', marginBottom: '8px' }}>Your Pattern Matches:</div>
                    {analysis.bayesianAdjustment.detailedAnalysis.components?.map((component, index) => (
                      <div key={index} style={{ 
                        backgroundColor: 'white',
                        border: '1px solid #e9ecef',
                        borderRadius: '4px',
                        padding: '8px',
                        marginBottom: '6px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <span style={{ fontWeight: '600', color: theme.colors.primary.main }}>
                            {component.component}
                          </span>
                          <span style={{ 
                            backgroundColor: theme.colors.primary.main,
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            fontSize: '11px',
                            fontWeight: '600'
                          }}>
                            {component.weight}
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                          <strong>Matched:</strong> {component.matchedPattern}
                        </div>
                        <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                          <strong>Your Value:</strong> {component.userValue} → 
                          <strong> Expected Score:</strong> {component.expectedScore}
                          {component.credibleRange && (
                            <span> (Range: {component.credibleRange})</span>
                          )}
                        </div>
                        {component.sampleSize && (
                          <div style={{ fontSize: '11px', color: '#6c757d' }}>
                            Based on {component.sampleSize} similar users
                            {component.prevalence && ` (${component.prevalence})`}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Agreement Analysis */}
                  {analysis.bayesianAdjustment.detailedAnalysis.agreementAnalysis && (
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontWeight: '600', marginBottom: '6px' }}>Pattern Agreement:</div>
                      <div style={{
                        backgroundColor: analysis.bayesianAdjustment.detailedAnalysis.agreementAnalysis.level === 'high-agreement' ? '#d4edda' :
                                       analysis.bayesianAdjustment.detailedAnalysis.agreementAnalysis.level === 'moderate-agreement' ? '#fff3cd' :
                                       analysis.bayesianAdjustment.detailedAnalysis.agreementAnalysis.level === 'low-agreement' ? '#f8d7da' : '#f1f3f4',
                        border: '1px solid ' + (
                          analysis.bayesianAdjustment.detailedAnalysis.agreementAnalysis.level === 'high-agreement' ? '#c3e6cb' :
                          analysis.bayesianAdjustment.detailedAnalysis.agreementAnalysis.level === 'moderate-agreement' ? '#ffeaa7' :
                          analysis.bayesianAdjustment.detailedAnalysis.agreementAnalysis.level === 'low-agreement' ? '#f5c6cb' : '#dee2e6'
                        ),
                        borderRadius: '4px',
                        padding: '8px',
                        fontSize: '12px'
                      }}>
                        <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                          {analysis.bayesianAdjustment.detailedAnalysis.agreementAnalysis.level.replace('-', ' ').toUpperCase()} 
                          ({(analysis.bayesianAdjustment.detailedAnalysis.agreementAnalysis.confidence * 100).toFixed(0)}%)
                        </div>
                        <div>{analysis.bayesianAdjustment.detailedAnalysis.agreementAnalysis.explanation}</div>
                      </div>
                    </div>
                  )}

                  {/* Final Summary */}
                  {analysis.bayesianAdjustment.detailedAnalysis.message && (
                    <div style={{
                      backgroundColor: 'white',
                      border: '1px solid #e9ecef',
                      borderRadius: '4px',
                      padding: '12px',
                      fontFamily: 'monospace',
                      fontSize: '11px',
                      lineHeight: '1.4',
                      whiteSpace: 'pre-line'
                    }}>
                      {analysis.bayesianAdjustment.detailedAnalysis.message}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Recommendations */}
          {analysis?.recommendations && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ 
                color: theme.colors.primary.main,
                fontSize: '20px',
                fontWeight: '600',
                marginBottom: '16px'
              }}>
                Improve Your Confidence
              </h3>

              {analysis.recommendations.nextConfidenceLevel && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ 
                    fontSize: '16px', 
                    fontWeight: '600',
                    color: theme.colors.secondary.black,
                    marginBottom: '8px'
                  }}>
                    Next Level: {analysis.recommendations.nextConfidenceLevel?.replace('-', ' ').toUpperCase() || 'Maximum Reached'}
                  </div>
                  <div style={{ 
                    fontSize: '14px', 
                    color: theme.colors.secondary.black,
                    marginBottom: '12px'
                  }}>
                    Add {analysis.recommendations.entriesNeeded} more entries ({analysis.recommendations.timeEstimate})
                  </div>
                  
                  {/* Progress Bar */}
                  <div style={{
                    backgroundColor: '#f1f2f6',
                    borderRadius: '10px',
                    height: '8px',
                    overflow: 'hidden',
                    marginBottom: '16px'
                  }}>
                    <div style={{
                      backgroundColor: theme.colors.primary.main,
                      height: '100%',
                      width: `${analysis.confidence.progressPercentage}%`,
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              )}

                             {/* Action Items */}
               {analysis.recommendations.actions?.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ 
                    fontSize: '16px', 
                    fontWeight: '600',
                    color: theme.colors.secondary.black,
                    marginBottom: '8px'
                  }}>
                    Recommended Actions:
                  </h4>
                  <ul style={{ 
                    margin: 0, 
                    paddingLeft: '20px',
                    color: theme.colors.secondary.black
                  }}>
                                         {analysis.recommendations.actions?.map((action, index) => (
                      <li key={index} style={{ marginBottom: '4px', fontSize: '14px' }}>
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

                             {/* Benefits */}
               {analysis.recommendations.benefits?.length > 0 && (
                <div>
                  <h4 style={{ 
                    fontSize: '16px', 
                    fontWeight: '600',
                    color: theme.colors.secondary.black,
                    marginBottom: '8px'
                  }}>
                    Benefits:
                  </h4>
                  <ul style={{ 
                    margin: 0, 
                    paddingLeft: '20px',
                    color: theme.colors.secondary.black
                  }}>
                                         {analysis.recommendations.benefits?.map((benefit, index) => (
                      <li key={index} style={{ marginBottom: '4px', fontSize: '14px' }}>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Column - Visualization */}
        <div>
          {/* Scatter Plot */}
          {scatterData && (userEntries.length > 0 || syntheticEntries.length > 0) && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ 
                color: theme.colors.primary.main,
                fontSize: '20px',
                fontWeight: '600',
                marginBottom: '16px'
              }}>
                Pressure vs Emotional Strength
              </h3>
              
              <div style={{ height: '400px' }}>
                <ScatterChart 
                  data={createChartData()}
                  options={{
                    scales: {
                      x: {
                        title: {
                          display: true,
                          text: 'Pressure Level',
                          color: theme.colors.primary.main,
                          font: {
                            family: theme.typography.body.fontFamily,
                            weight: 500,
                          },
                        },
                        min: 0,
                        max: 10
                      },
                      y: {
                        title: {
                          display: true,
                          text: 'Emotional Strength',
                          color: theme.colors.primary.main,
                          font: {
                            family: theme.typography.body.fontFamily,
                            weight: 500,
                          },
                        },
                        min: 0,
                        max: 10
                      }
                    },
                    plugins: {
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            const point = context.raw
                            return `Pressure: ${point.x}, Emotional: ${point.y.toFixed(1)}`
                          }
                        }
                      }
                    }
                  }}
                />
              </div>
              
              <div style={{ 
                fontSize: '12px', 
                color: theme.colors.secondary.black,
                marginTop: '12px',
                fontStyle: 'italic'
              }}>
                Higher emotional strength at higher pressure levels indicates better resilience.
                Triangles represent generated examples to help visualize patterns.
              </div>
            </div>
          )}

          {/* Empty State */}
          {(!scatterData || (userEntries.length === 0 && syntheticEntries.length === 0)) && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '48px',
              textAlign: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}>
              <div style={{ 
                fontSize: '48px', 
                marginBottom: '16px',
                opacity: 0.5
              }}>
                📊
              </div>
              <h3 style={{ 
                color: theme.colors.primary.main,
                fontSize: '20px',
                marginBottom: '8px'
              }}>
                No Data Yet
              </h3>
              <p style={{ 
                color: theme.colors.secondary.black,
                fontSize: '14px'
              }}>
                Add your journal entries or generate synthetic examples to see your mental resilience analysis and scatter plot visualization.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default NewUserPage 